// ─────────────────────────────────────────────────────────────────────────────
// Cards.tsx — the V2 Writer page's card kit (Writer.dc.html :104-200): rows,
// items (with a chat thread and a link), bars, meter, the season-vision box and
// the plain-document script. Every inline style is the design's own CSS text,
// carried verbatim through sx() (Buckly's primitive, with Sawyer's own font
// family names), so the port can be checked declaration for declaration.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import type { Card, DocLine } from './compose';

const ALIASES: ReadonlyArray<[string, string]> = [
  ["'Inter'", "'Sawyer Inter'"],
  ["'JetBrains Mono'", "'Sawyer JetBrains Mono'"],
];
const cache = new Map<string, React.CSSProperties>();
function camel(prop: string): string {
  const p = prop.startsWith('-webkit-') ? 'Webkit-' + prop.slice(8) : prop;
  return p.replace(/-([a-z])/g, (_m, c: string) => c.toUpperCase());
}
/** The design's CSS text → a React style object (a repeated property keeps the last value). */
export function sx(css: string): React.CSSProperties {
  const hit = cache.get(css);
  if (hit) return hit;
  const out: Record<string, string> = {};
  let depth = 0, start = 0;
  const decls: string[] = [];
  for (let i = 0; i < css.length; i++) {
    const c = css[i];
    if (c === '(') depth++;
    else if (c === ')') depth--;
    else if (c === ';' && depth === 0) { decls.push(css.slice(start, i)); start = i + 1; }
  }
  decls.push(css.slice(start));
  for (const d of decls) {
    const i = d.indexOf(':');
    if (i < 0) continue;
    const prop = d.slice(0, i).trim();
    let value = d.slice(i + 1).trim();
    if (!prop || !value) continue;
    if (prop === 'font-family') for (const [from, to] of ALIASES) value = value.split(from).join(to);
    out[camel(prop)] = value;
  }
  cache.set(css, out as React.CSSProperties);
  return out as React.CSSProperties;
}

/** The design's chat bubble (renderVals `bubble`). */
export function bubble(mine: boolean, mw?: string): React.CSSProperties {
  return sx('display:flex;flex-direction:column;gap:4px;max-width:' + (mw || '78%') + ';padding:12px 14px;border-radius:12px;' + (mine ? 'align-self:flex-end;background:rgba(60,150,230,0.18);border:1px solid rgba(60,150,230,0.35)' : 'align-self:flex-start;background:rgba(255,255,255,0.035);border:1px solid rgba(255,255,255,0.08)'));
}

export function DocView({ doc }: { doc: DocLine[] }) {
  return (
    <div style={sx('width: 100%; max-width: 720px; margin: 0 auto; padding: 14px 34px 30px; border-radius: 10px; background: rgba(4,6,14,0.6); border: 1px solid rgba(255,255,255,0.07); display: flex; flex-direction: column; gap: 10px')}>
      {doc.map((d, i) => (
        <React.Fragment key={i}>
          {d.isHead ? (
            <div style={sx('display: flex; justify-content: space-between; align-items: baseline; gap: 12px; margin-top: 14px; padding-bottom: 6px; border-bottom: 1px solid rgba(95,184,240,0.18)')}>
              <span style={sx("font-family: 'JetBrains Mono', monospace; font-size: 12px; font-weight: 600; letter-spacing: 0.14em; color: #6fc0f2")}>{d.label}</span>
              <span style={sx("font-family: 'JetBrains Mono', monospace; font-size: 12px; color: rgba(220,228,230,0.62)")}>{d.tc}</span>
            </div>
          ) : null}
          {d.isAction ? (
            <p style={sx("margin: 0; font-family: 'Spectral', Georgia, serif; font-size: 16.5px; line-height: 1.6; color: rgba(232,238,240,0.9); text-wrap: pretty")}>{d.text}</p>
          ) : null}
          {d.isLine ? (
            <div style={sx('display: flex; flex-direction: column; align-items: center; gap: 3px; padding: 6px 0')}>
              <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 11.5px; letter-spacing: 0.12em"), color: d.cc }}>{d.who}</span>
              <span style={sx("font-family: 'Spectral', Georgia, serif; font-size: 16.5px; color: #f4f6f7")}>{d.text}</span>
            </div>
          ) : null}
        </React.Fragment>
      ))}
    </div>
  );
}

export interface VisionProps {
  text: string | null | undefined;
  sent: { at: string; t: string; ok: boolean; why?: string }[];
  draft: string;
  setDraft: (v: string) => void;
  onSend: () => void;
  busy: boolean;
  error: string | null;
}

export function CardView({ c, vision }: { c: Card; vision?: VisionProps }) {
  return (
    <div className={c.wide ? 'card wide' : 'card'} style={sx('display: flex; flex-direction: column; gap: 12px; min-width: 0')}>
      <div style={sx('display: flex; justify-content: space-between; align-items: baseline; gap: 10px; flex-wrap: wrap')}>
        <span className="lbl">{c.lbl}</span>
        <span className="src">{c.aside}</span>
      </div>
      {c.rows ? (
        <div style={sx('display: flex; flex-direction: column')}>
          {c.rows.map((r, i) => (
            <div key={i} style={sx('display: flex; justify-content: space-between; gap: 14px; padding: 9px 0; border-bottom: 1px solid rgba(255,255,255,0.05)')}>
              <span style={sx('font-size: 13.5px; color: rgba(220,228,230,0.85)')}>{r.k}</span>
              <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 12.5px; text-align: right"), color: r.c }}>{r.v}</span>
            </div>
          ))}
        </div>
      ) : null}
      {c.items ? (
        <div style={sx('display: flex; flex-direction: column; gap: 10px')}>
          {c.items.map((it, i) => (
            <div key={i} style={sx('padding: 12px 14px; border-radius: 10px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); display: flex; flex-direction: column; gap: 5px')}>
              <div style={sx('display: flex; justify-content: space-between; gap: 10px; flex-wrap: wrap')}>
                <span style={sx('font-size: 14px; font-weight: 600; color: #f4f6f7')}>{it.t}</span>
                <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 11px; letter-spacing: 0.1em"), color: it.tc }}>{it.tag}</span>
              </div>
              <span style={sx('font-size: 13px; line-height: 1.5; color: rgba(220,228,230,0.75)')}>{it.s}</span>
              {it.hasMeta ? <span className="src">{it.meta}</span> : null}
              {it.hasThread ? (
                <div style={sx('display: flex; flex-direction: column; gap: 8px; padding-top: 6px')}>
                  {it.thread.map((m, j) => (
                    <div key={j} style={bubble(m.mine, '88%')}>
                      <span className="src">{m.who}</span>
                      <span style={sx('font-size: 13.5px; line-height: 1.5; color: #e8eef0')}>{m.text}</span>
                    </div>
                  ))}
                </div>
              ) : null}
              {it.hasLink ? (
                <button onClick={it.go || undefined} style={sx("align-self: flex-start; padding: 4px 0; min-height: 28px; background: none; border: none; cursor: pointer; font-family: 'JetBrains Mono', monospace; font-size: 12px; color: #5fb8f0")}>{it.link} →</button>
              ) : null}
            </div>
          ))}
        </div>
      ) : null}
      {c.bars ? (
        <div style={sx('display: flex; flex-direction: column; gap: 14px; padding-top: 4px')}>
          {c.bars.map((g, i) => (
            <div key={i} style={sx('display: flex; flex-direction: column; gap: 7px')}>
              <div style={sx('display: flex; justify-content: space-between; gap: 12px')}>
                <span style={sx('font-size: 13.5px; color: rgba(220,228,230,0.85)')}>{g.t}</span>
                <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 12.5px"), color: g.vc }}>{g.v}</span>
              </div>
              <div className="bar"><div style={sx('height:100%;width:' + g.pct + '%;background:' + g.color)} /></div>
            </div>
          ))}
        </div>
      ) : null}
      {c.meter ? (
        <div style={sx('display: flex; flex-direction: column; gap: 8px')}>
          <span style={sx('font-size: 28px; font-weight: 600; color: #f4f6f7')}>{c.meter.mv} <span style={sx('font-size: 14px; font-weight: 400; color: rgba(220,228,230,0.62)')}>{c.meter.mmax}</span></span>
          <div className="bar"><div style={sx('height:100%;width:' + c.meter.pct + '%;background:#5fb8f0')} /></div>
        </div>
      ) : null}
      {c.vision && vision ? (
        <div style={sx('display: flex; flex-direction: column; gap: 12px')}>
          <p style={sx("margin: 0; padding: 16px 18px; border-radius: 10px; background: rgba(255,255,255,0.025); border: 1px solid rgba(255,255,255,0.06); font-family: 'Spectral', Georgia, serif; font-size: 16.5px; line-height: 1.6; color: rgba(232,238,240,0.92); white-space: pre-wrap; text-wrap: pretty")}>
            {vision.text ? vision.text : 'No season vision yet. Tell Sawyer what this season is, and he plans it from your words.'}
          </p>
          {vision.sent.map((v, i) => (
            <div key={i} style={sx('display: flex; gap: 10px; font-size: 13px; line-height: 1.5; color: rgba(220,228,230,0.85)')}>
              <span style={{ ...sx("font-family: 'JetBrains Mono', monospace; font-size: 12px; white-space: nowrap"), color: v.ok ? '#5fd38a' : '#eb8a7f' }}>{v.ok ? '✓' : '✕'} {v.at}</span>
              <span>{v.t}{v.why && !v.ok ? ` — ${v.why}` : ''}</span>
            </div>
          ))}
          <div style={sx('display: flex; gap: 10px; flex-wrap: wrap; align-items: flex-end')}>
            <textarea rows={3} aria-label="Tell Sawyer what to add or remove" placeholder="Tell Sawyer what to add or remove…" value={vision.draft}
              onChange={(e) => vision.setDraft(e.target.value)}
              style={sx("flex: 1 1 320px; font-family: 'Inter', sans-serif; font-size: 14px; line-height: 1.5; padding: 10px 12px; border-radius: 10px; border: 1px solid rgba(95,184,240,0.3); background: rgba(4,6,14,0.75); color: #e8eef0; resize: vertical")} />
            <button className="btn" onClick={vision.onSend} disabled={vision.busy} style={sx('min-height: 44px')}>Send</button>
          </div>
          {vision.error ? <span className="src" style={{ color: '#eb8a7f' }}>{vision.error}</span> : null}
        </div>
      ) : null}
      {c.doc ? <DocView doc={c.doc} /> : null}
      <span className="src">{c.src}</span>
    </div>
  );
}
